import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { LicenseService } from '@core/services';
import { AuthenticationService } from '@core/services';

@Component({
  selector: 'app-expired-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './expired-overlay.component.html',
  styleUrls: ['./expired-overlay.component.scss'],
  animations: [
    trigger('overlayFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('350ms 200ms ease-out', style({ opacity: 1 })),
      ]),
    ]),
    trigger('cardPop', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.92) translateY(16px)' }),
        animate('400ms 250ms cubic-bezier(0.16, 1, 0.3, 1)', style({ opacity: 1, transform: 'scale(1) translateY(0)' })),
      ]),
    ]),
  ],
})
export class ExpiredOverlayComponent implements OnInit, OnDestroy {
  isBlocked = false;
  isSuspended = false;
  private _sub?: Subscription;

  constructor(
    private readonly _licenseService: LicenseService,
    private readonly _authService: AuthenticationService,
    private readonly _router: Router,
  ) {}

  ngOnInit(): void {
    this._sub = this._licenseService.context$.subscribe(() => {
      this.isBlocked = this._licenseService.isBlocked;
      this.isSuspended = this._licenseService.isSuspended;
    });
    this.isBlocked = this._licenseService.isBlocked;
    this.isSuspended = this._licenseService.isSuspended;
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
  }

  get badgeText(): string {
    if (this.isSuspended) return 'Account Suspended';
    if (this._licenseService.hasNoLicense) return 'No License';
    
    const type = this._licenseService.licenseType;
    if (type === 'free_trial') return 'Trial Ended';
    return 'Plan Expired';
  }

  get title(): string {
    if (this.isSuspended) return 'Your account has been suspended';
    if (this._licenseService.hasNoLicense) return 'Access Restricted';
    
    const type = this._licenseService.licenseType;
    if (type === 'free_trial') return 'Your trial has ended';
    return 'Your plan has expired';
  }

  get subtitle(): string {
    if (this.isSuspended) {
      return 'Your account access has been suspended. Please contact our support team to resolve this.';
    }
    if (this._licenseService.hasNoLicense) {
      return 'Please choose a plan to access the dashboard.';
    }
    return 'Upgrade to a paid plan to continue using convverse and keep your data, recordings, and insights.';
  }

  get ctaLabel(): string {
    if (this.isSuspended) return 'Contact support';
    if (this._licenseService.hasNoLicense) return 'Choose a Plan';
    return 'Continue with Convverse';
  }

  openCta(): void {
    if (!this.isSuspended && this._licenseService.hasNoLicense) {
      this._router.navigate(['/choose-plan']);
      return;
    }
    window.open('https://convverse.ai/contact-us', '_blank', 'noopener,noreferrer');
  }

  logout(): void {
    this._authService.clearUserDetail();
    this._router.navigate(['/login']);
  }
}
